import { useState } from "react";
import { MoreHorizontal, Filter } from "lucide-react";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
    Button,
    Combobox,
    Drawer,
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
    Modal,
    Popover,
    PopoverContent,
    PopoverTrigger,
    SearchableDropdown,
    Tooltip,
} from "@maeiz/ui";
import { DocPage, Section, Demo, SpecTable } from "../../components/DocChrome";

export function OverlaysPage() {
    const [modalOpen, setModalOpen] = useState(false);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [combo, setCombo] = useState("dha");
    const [city, setCity] = useState("khi");

    return (
        <DocPage
            title="Overlays"
            description="Animated Drawer/Modal. Collision-aware Popover/Menu/Combobox (flip top/right/left/bottom). Thin scrollbars."
        >
            <Section title="Menus & popovers">
                <Demo className="flex flex-wrap gap-3">
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant="outline" icon={MoreHorizontal}>
                                Menu
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent>
                            <DropdownMenuLabel>Actions</DropdownMenuLabel>
                            <DropdownMenuItem>Edit</DropdownMenuItem>
                            <DropdownMenuItem>Duplicate</DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem destructive>Delete</DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>

                    <Popover>
                        <PopoverTrigger asChild>
                            <Button variant="outline" icon={Filter}>
                                Popover
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent>
                            <p className="text-sm font-semibold text-neutral-dark dark:text-white">
                                Filters
                            </p>
                            <p className="mt-1 text-sm text-neutral-secondary">
                                Compose FormFields inside the popover.
                            </p>
                        </PopoverContent>
                    </Popover>

                    <Button onClick={() => setModalOpen(true)}>Modal</Button>
                    <Button variant="outline" onClick={() => setDrawerOpen(true)}>
                        Drawer
                    </Button>
                    <Tooltip content="Helpful hint">
                        <Button variant="ghost">Tooltip</Button>
                    </Tooltip>
                </Demo>
            </Section>

            <Section title="Searchable">
                <Demo className="grid max-w-sm gap-4">
                    <Combobox
                        value={combo}
                        onChange={setCombo}
                        options={[
                            { value: "dha", label: "DHA Phase 5" },
                            { value: "gulshan", label: "Gulshan" },
                            { value: "clifton", label: "Clifton" },
                        ]}
                    />
                    <SearchableDropdown
                        value={city}
                        onChange={setCity}
                        options={[
                            { value: "khi", label: "Karachi" },
                            { value: "lhr", label: "Lahore" },
                            { value: "isb", label: "Islamabad" },
                        ]}
                    />
                </Demo>
            </Section>

            <Section title="Accordion">
                <Demo>
                    <Accordion type="single" collapsible className="w-full max-w-lg">
                        <AccordionItem value="a">
                            <AccordionTrigger>Billing</AccordionTrigger>
                            <AccordionContent>Plan, invoices, Safepay.</AccordionContent>
                        </AccordionItem>
                        <AccordionItem value="b">
                            <AccordionTrigger>Notifications</AccordionTrigger>
                            <AccordionContent>Email and push preferences.</AccordionContent>
                        </AccordionItem>
                    </Accordion>
                </Demo>
            </Section>

            <Modal
                isOpen={modalOpen}
                onClose={() => setModalOpen(false)}
                title="Confirm action"
                footer={
                    <div className="flex justify-end gap-2">
                        <Button variant="ghost" onClick={() => setModalOpen(false)}>
                            Cancel
                        </Button>
                        <Button onClick={() => setModalOpen(false)}>Confirm</Button>
                    </div>
                }
            >
                <p className="text-sm text-neutral-secondary">
                    Sticky header and footer. Body scrolls with a thin scrollbar when content is
                    long.
                </p>
                <div className="mt-4 space-y-2">
                    {Array.from({ length: 12 }).map((_, i) => (
                        <p
                            key={i}
                            className="rounded-lg bg-neutral-light px-3 py-2 text-sm dark:bg-white/5"
                        >
                            Scroll row {i + 1}
                        </p>
                    ))}
                </div>
            </Modal>
            <Drawer
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                title="Detail"
                footer={
                    <Button fullWidth onClick={() => setDrawerOpen(false)}>
                        Done
                    </Button>
                }
            >
                <p className="text-sm text-neutral-secondary">
                    Slides in/out with opacity + transform. Escape closes.
                </p>
                <div className="mt-4 space-y-2">
                    {Array.from({ length: 8 }).map((_, i) => (
                        <p
                            key={i}
                            className="rounded-lg bg-neutral-light px-3 py-2 text-sm dark:bg-white/5"
                        >
                            Detail row {i + 1}
                        </p>
                    ))}
                </div>
            </Drawer>

            <Section title="Behavior">
                <SpecTable
                    rows={[
                        ["asChild", "Button forwardRef — required for Radix triggers"],
                        ["Collision", "avoidCollisions + padding — flips to fit viewport"],
                        ["Motion", "opacity + transform only; reduced-motion safe"],
                        ["Modal/Drawer", "Sticky chrome + thin body scroll"],
                    ]}
                />
            </Section>
        </DocPage>
    );
}
